<script setup>
import { ref, reactive, computed, watchEffect, inject } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import {
  Listbox,
  ListboxLabel,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
  RadioGroup,
  RadioGroupLabel,
  RadioGroupOption, SwitchLabel, Switch, SwitchGroup,
} from '@headlessui/vue'
import { canvasCountryList } from '@/helpers/countries.js'
import { isValidEmail } from '@/helpers/general.js'

import { useUserStore } from '@/stores/UserStore'

import creditCardType from 'credit-card-type';

const addLineBreaks = (text) => {
  if (!text) return ''
  // return text.replace(/\n/g, "<br>\n")

  const lines = text.split(/[\n]+/)
  return lines.map((line) => `<p class='mb-2'>${line}</p>`).join('\n\n')
}

const siteConfig = inject('siteConfig')

const router = useRouter()

const userStore = useUserStore()

const errorMessage = ref(null)
const billingErrorMessage = ref(null)

const submitting = ref(false)
const savePayment = ref(false)
const savedPayments = ref(userStore.savedPayments || [])
const selectedPayment = ref(null)
const acceptProcessingFee = ref(false)

// const signedIn = ref(true)
// const donationAmount = ref(null);
// const customAmounts = ref(siteConfig.features?.donations?.amounts || [25, 50, 100, 250, 500]);
const minimumAmount = ref(siteConfig.features?.donations?.minimumAmount || 10,)
const donationAmounts = ref(
  siteConfig.features?.donations?.amounts || [25, 50, 100, 250, 500],
)

const donation = reactive({
  amount: donationAmounts.value[0],
  frequency: 'one-time',
  anonymous: false,
  dedicate: false,
  dedicationType: 'In honor of',
  dedication: '',
})

const billing = reactive({})

const paysProcessingFee = computed(() => {
  const processingFeeAccepted = siteConfig.features.processingFeeOptional ? acceptProcessingFee.value : true
  return processingFeeAccepted && siteConfig.features.processingFeeRate && selectedPayment.value !== 'ACH' && siteConfig.features.donationProcessingFee
})

const processingFee = computed(() => {
  return paysProcessingFee.value ? calculateProcessingFee() : 0
})

const calculateProcessingFee = () => {
  if (!amount.value || !acceptProcessingFee.value) return 0
  const rate = siteConfig.features.processingFeeRate
  let fee = 0
  if (rate) {
    fee = Math.max((amount.value * rate), 0.00)
  }
  return fee
}

const getCardType = (cardNumber) => {
  const types = creditCardType(cardNumber)
  let value = types.length ? types[0].niceType : ''
  if (value === 'American Express'){
    value = 'Amex'
  }
  return value
}

const amount = computed(() => {
  return donation.amount !== 'other' ? donation.amount : donation.otherAmount
})

const effectiveAmount = computed(() => {
  return amount.value + processingFee.value
})

const canMakeDonation = computed(() => {
  if (donation.frequency === 'monthly') {
    if (userStore.account && userStore.loggedIn) {
      return true
    }
    return false
  }
  return true
})

const canSubmit = computed(() => {
  let ok = true

  ok =
    (donation.amount && donation.amount !== 'other') ||
    (donation.amount === 'other' && donation.otherAmount)
  return ok
})

const shouldSavePayment = computed(() => {
  return savePayment.value || donation.frequency === 'monthly'
})

const frequencies = ref([
  { name: 'One time donation', value: 'one-time' },
  { name: 'Monthly recurring', value: 'monthly' },
])

// for (let i = 1; i <= 31; i++) {
//   let s = ['th', 'st', 'nd', 'rd'];
//   let v = i % 100;
//   let option = `${i}<sup>${s[(v - 20) % 10] || s[v] || s[0]}</sup>`;
//   chargeDayDroplist.value.options.push(option);
// }

const countriesDroplist = ref({
  label: 'Country',
  selectedItem: 'United States',
  direction: '',
  options: [...canvasCountryList.map((c) => c.name)],
  optionsOpened: false,
})

const selectCountry = (option) => {
  countriesDroplist.value.selectedItem = option
  countriesDroplist.value.optionsOpened = false
}
const closeCountriesDroplist = () =>
  (countriesDroplist.value.optionsOpened = false)

const selectedCampaign = computed(() => {
  const match = userStore.defaultDonationCampaign

  if (match) return match.name

  return null
})

const campaignsDroplist = computed(() => {
  console.log(
    'campaigns in donate',
    userStore.campaigns.map((c) => c.name),
  )
  return {
    label: 'Apply my donation to',
    selectedItem: selectedCampaign.value || 'Please select',
    direction: '',
    options: userStore.campaigns.map((c) => c.name),
  }
})

const paymentProfile = () => {
  return selectedPayment.value === null || selectedPayment.value === 'ACH' ? null : selectedPayment.value.id
}

const updateSelectedCampaign = (campaign) => {
  if (!campaign){
    campaign = siteConfig.features.campaigns?.defaultId
  }
  const match = userStore.campaigns.find((c) => c.name === campaign || c.campaignId === campaign)
  if (!match) return
  donation.campaignId = match.campaignId
  donation.campaignName = match.name
  donation.glAccount = match.glacct
  donation.glclass = match.glclass
  donation.glsubclass = match.glsubclass
}

// const showingAddress = ref(true) // was false
const submitPayment = async () => {
  if (submitting.value) return

  submitting.value = true

  if (!donation.campaignId){
    updateSelectedCampaign()
  }

  errorMessage.value = ''
  billingErrorMessage.value = ''
  billing.paymentType = null

  if (!donation.campaignId){
    updateSelectedCampaign()
  }

  if (donation.amount === 'other') {
    if (!donation.otherAmount) {
      errorMessage.value = 'Please enter a donation amount'
      submitting.value = false
      return
    }
    if (donation.otherAmount < minimumAmount.value) {
      errorMessage.value = 'Please enter a donation amount of at least $' + minimumAmount.value
      submitting.value = false
      return
    }
  }

  if (donation.dedicate && !donation.dedication) {
    errorMessage.value = 'Please enter a name for the dedication'
    submitting.value = false
    return
  }
  if (!selectedPayment.value) {

    if (!billing.cardNumber) {
      billingErrorMessage.value = 'Please provide a valid card number'
      submitting.value = false
      return
    }
    if (!billing.cardName) {
      billingErrorMessage.value = 'Please provide name for the credit card'
      submitting.value = false
      return
    }
    if (!billing.expDate) {
      billingErrorMessage.value = 'Please provide a valid card number'
      submitting.value = false
      return
    }
    if (!billing.cvmValue) {
      billingErrorMessage.value = 'Please provide a CVV code'
      submitting.value = false
      return
    }
    if (!billing.address1) {
      billingErrorMessage.value = 'Please provide a valid address'
      submitting.value = false
      return
    }
    if (!billing.city) {
      billingErrorMessage.value = 'Please provide a city'
      submitting.value = false
      return
    }
    if (!billing.state) {
      billingErrorMessage.value = 'Please provide a valid state'
      submitting.value = false
      return
    }
    if (!billing.zip) {
      billingErrorMessage.value = 'Please provide a valid zip code'
      submitting.value = false
      return
    }

    if (!userStore.loggedIn && (!billing.email || !isValidEmail(billing.email))) {
      billingErrorMessage.value = 'Please provide a valid email address'
      submitting.value = false
      return
    }

    if (!userStore.loggedIn && !billing.phoneNumber) {
      billingErrorMessage.value = 'Please provide a phone number'
      submitting.value = false
      return
    }

    try {
      billing.cardType = getCardType(billing.cardNumber)
    } catch (e) {
      console.log('error getting card type', e)
    }
  }
  else if (selectedPayment.value === 'ACH') {
    if (!billing.routingNumber || billing.routingNumber.length !== 9) {
      errorMessage.value = 'Please provide a valid routing number'
      submitting.value = false
      return
    }
    if (!billing.accountNumber || billing.accountNumber.length < 4) {
      errorMessage.value = 'Please provide a valid account number'
      submitting.value = false
      return
    }
    if (!billing.cardName) {
      errorMessage.value = 'Please provide the account holder name for the account'
      submitting.value = false
      return
    }
    billing.paymentType = 'ACH'
  }

  grecaptcha.ready(function() {
    grecaptcha.execute('6Lfqs9kpAAAAAH5uMUe1cPkA50abCQhhdWmHGc03', {action: 'donation'}).then(function(token) {
      const payload = {
        captchaToken: token,
        personId: userStore.account?.personId || 'guest',
        account: userStore.account,
        billingData: !paymentProfile() ? billing : {},
        savePayment: shouldSavePayment.value,
        paymentProfile: paymentProfile(),
        donationData: donation,
        total: effectiveAmount.value,
      }
      const endpoint = `${siteConfig.apiBaseUrl}/users/${
        userStore.account?.personId || 'guest'
      }/donations`
      console.log('donations payload', payload)

      const redactedBillingData = {...payload.billingData}
      redactedBillingData.cardNumber = redactedBillingData.cardNumber?.replace(/\d(?=\d{4})/g, '*')
      redactedBillingData.cvmValue = redactedBillingData.cvmValue?.replace(/\d/g, '*')
      redactedBillingData.routingNumber = redactedBillingData.routingNumber?.replace(/\d/g, '*')
      redactedBillingData.accountNumber = redactedBillingData.accountNumber?.replace(/\d/g, '*')

      axios
        .post(
          endpoint,
          payload
        )
        .then((response) => {
          console.log('donations response.data', response.data)
          const data = response.data
          if (!data.ok) {
            if (data.error) {
              errorMessage.value = data.error
            } else {
              errorMessage.value =
                'We\'re sorry, an error has occurred. Please try again later.'
            }
            submitting.value = false
            return
          }

          submitting.value = false

          router.push('/donate/confirmation')
        })
        .catch((error) => {
          console.log('order error', error)
          errorMessage.value =
            'We\'re sorry, an error has occurred. Please try again later.'
          submitting.value = false
        })
        });
      });
}

watchEffect(() => {
  if (errorMessage.value || billingErrorMessage.value) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
})

</script>

<template>
  <section class="store-donate">
    <div
      class="hero-banner"
      :style="`background-image: url(/sites/${siteConfig.key}/${siteConfig.assets?.banners?.donate})`"
    >
      <div class="container">
        <h1>Donate</h1>
      </div>
    </div>

    <div class="container">
      <form class="donate-form">
        <div class="form-info">
          <h1 class="page-heading">
            {{
              siteConfig.copy?.donate?.title ||
                `Make a Gift to ${siteConfig.org.name}`
            }}
          </h1>
          <p
            class="page-description text-left"
            v-if="siteConfig.copy?.donate?.blurb"
            v-html="addLineBreaks(siteConfig.copy.donate.blurb)"
          ></p>
          <p class="page-description" v-else>
            Thank you so much for donating to {{ siteConfig.org.name }}. Please
            consider supporting us by making a recurring monthly donation so we
            can continue our work for years to come.
          </p>
        </div>

        <div class="donation-detail">
          <h2 class="section-heading xlarge-text">Donation Detail</h2>
          <p v-if="errorMessage" class="font-semibold text-red-500 mb-4">
            <span>{{ errorMessage }}</span>
          </p>
          <div class="row">
            <div class="first column">
              <div class="donation-box">
                <h4>
                  I want to support {{ siteConfig.org.name }} through a gift of:
                </h4>
                <!-- <div class="flex items-center check-input">
                  <label>
                    <input type="checkbox" />
                    I prefer that {{siteConfig.org.name}} does NOT publicly acknowledge my support
                  </label>
                </div> -->

                <!-- <h4 class="small-bold">Amount</h4> -->
                <!-- <div class="donation-amount">
                  <label
                    class="radio-input"
                    v-for="amount in donationAmounts"
                    :key="amount"
                  >
                    <input
                      type="radio"
                      v-model="donation.amount"
                      :value="amount"
                      :checked="donation.amount === amount"
                    />
                    &dollar;{{ amount }}
                  </label>

                  <div class="other-amount">
                    <label class="radio-input">
                      <input
                        type="radio"
                        v-model="donation.amount"
                        :value="`other`"
                        :checked="donation.amount === 'other'"
                      />
                      Other
                    </label>
                    <div class="inp-box">
                      <input
                        type="number"
                        placeholder="0.00"
                        class="form-control"
                        :disabled="donation.amount !== 'other'"
                        v-model="donation.otherAmount"
                      />
                      <span class="special-icon">&dollar;</span>
                    </div>
                  </div>
                </div> -->
                <RadioGroup v-model="donation.amount" class="donation-amount">
                  <RadioGroupOption
                    v-for="(donationAmount, index) in donationAmounts"
                    :value="donationAmount"
                    :key="index"
                    class="radio-input"
                    v-slot="{ checked }"
                    as="label"
                  >
                    <input type="radio" :checked="checked" />
                    &dollar;{{ donationAmount }}
                  </RadioGroupOption>
                  <div class="other-amount">
                    <RadioGroupOption
                      :value="`other`"
                      class="radio-input"
                      v-slot="{ checked }"
                      as="label"
                    >
                      <input type="radio" :checked="checked" />
                      Other
                    </RadioGroupOption>
                    <div class="inp-box">
                      <input
                        type="number"
                        placeholder="0.00"
                        class="form-control"
                        :disabled="donation.amount !== 'other'"
                        v-model="donation.otherAmount"
                      />
                      <span class="special-icon">&dollar;</span>
                    </div>
                  </div>
                </RadioGroup>

                <!-- <BaseDropdown
                  v-if="userStore.campaigns.length && siteConfig.features.campaigns.enabled"
                  :dropdown="campaignsDroplist"
                  class="gray-bg mt-4"
                  @updateSelected="updateSelectedCampaign"
                /> -->

                <!-- XXX TODO FIXME -->
                <!-- Fixed -->
                <Listbox
                  v-if="
                    userStore.campaigns.length &&
                      siteConfig.features.campaigns.enabled
                  "
                  v-model="campaignsDroplist.selectedItem"
                  @update:modelValue="updateSelectedCampaign"
                  v-slot="{ open }"
                  name="donationPurpose"
                  class="base-listbox"
                  as="div"
                >
                  <div class="listbox-overlay" v-if="open"></div>
                  <div class="relative">
                    <ListboxLabel class="inline-block small-bold mb-2">
                      {{ campaignsDroplist.label }}
                    </ListboxLabel>
                    <ListboxButton
                      class="listbox-button !bg-gray-50 !rounded-lg"
                      :class="{
                        'text-secondary-text':
                          campaignsDroplist.selectedItem === 'Please select',
                      }"
                    >
                      <span class="block truncate">{{
                        campaignsDroplist.selectedItem ||
                          campaignsDroplist.options[0]
                      }}</span>
                      <span class="selector-icon">
                        <Icon
                          icon="heroicons-outline:selector"
                          class="h-5 w-5 text-secondary-text"
                        />
                      </span>
                    </ListboxButton>
                    <transition name="fade-fast">
                      <ListboxOptions
                        class="listbox-options"
                        static
                        v-show="open"
                      >
                        <ListboxOption
                          v-slot="{ active }"
                          v-for="(option, index) in campaignsDroplist.options"
                          :value="option"
                          :key="index"
                          as="template"
                        >
                          <li
                            class="listbox-option small-regular"
                            :class="[
                              { 'bg-gray-50': active },
                              {
                                'font-bold text-accent-1':
                                  campaignsDroplist.selectedItem === option,
                              },
                            ]"
                          >
                            {{ option }}
                          </li>
                        </ListboxOption>
                      </ListboxOptions>
                    </transition>
                  </div>
                </Listbox>

                <div class="mt-4">
                  <label>
                    <input type="checkbox" v-model="donation.anonymous" />
                    &nbsp; I would like to make my donation anonymously
                  </label>
                </div>
              </div>
            </div>
            <div class="second column">
              <!-- <h4 class="small-bold">Frequency</h4>
              <div class="recurring-types">
                <label
                  class="radio-input"
                  v-for="(freq, index) in frequencies"
                  :key="freq"
                >
                  <input
                    type="radio"
                    v-model="donation.frequency"
                    :value="freq.value"
                    :checked="donation.frequency === freq.value"
                  />
                  {{freq.name}}
                </label>
              </div> -->
              <RadioGroup v-model="donation.frequency">
                <RadioGroupLabel class="small-bold" as="h4">
                  Frequency
                </RadioGroupLabel>
                <div class="recurring-types">
                  <RadioGroupOption
                    v-for="(freq, index) in frequencies"
                    :value="freq.value"
                    :key="index"
                    class="radio-input"
                    v-slot="{ checked }"
                    as="label"
                  >
                    <input type="radio" :checked="checked" />
                    {{ freq.name }}
                  </RadioGroupOption>
                </div>
              </RadioGroup>

              <p
                v-if="
                  siteConfig.key === 'chesapeake' &&
                    donation.frequency === 'monthly'
                "
              >
                <small>Your credit card will be charged for the amount requested
                  each month on the same day of the month as your first
                  donation.</small>
              </p>
              <p v-if="!canMakeDonation">
                To make a recurrring donation please
                <router-link
                  to="/sign-in?backTo=/donate"
                  class="text-accent-1 hover:text-accent-2"
                >
                  sign to your account
                </router-link>
                or
                <router-link
                  to="/create/account?backTo=/donate"
                  class="text-accent-1 hover:text-accent-2"
                >
                  create a new account
                </router-link>
                .
              </p>
              <label v-if="canMakeDonation">
                <input type="checkbox" v-model="donation.dedicate" />
                &nbsp;
                <strong>
                  I would like to dedicate my donation in honor or in memory of
                  someone
                </strong>
              </label>
              <div class="text-sm mt-4" v-if="donation.dedicate">
                <!-- <div>
                  <label class="radio-input">
                    <input
                      type="radio"
                      v-model="donation.dedicationType"
                      value="In honor of"
                      :checked="donation.dedicationType === 'In honor of'"
                    />
                    In honor of...
                  </label>
                  <label class="radio-input ml-6">
                    <input
                      type="radio"
                      v-model="donation.dedicationType"
                      value="In memory of"
                      :checked="donation.dedicationType === 'In memory of'"
                    />
                    In memory of...
                  </label>
                </div> -->
                <RadioGroup
                  v-model="donation.dedicationType"
                  class="flex items-center gap-x-6"
                >
                  <RadioGroupOption
                    v-slot="{ checked }"
                    value="In honor of"
                    class="radio-input"
                    as="label"
                  >
                    <input type="radio" :checked="checked" />
                    In honor of...
                  </RadioGroupOption>
                  <RadioGroupOption
                    v-slot="{ checked }"
                    value="In memory of"
                    class="radio-input"
                    as="label"
                  >
                    <input type="radio" :checked="checked" />
                    In memory of...
                  </RadioGroupOption>
                </RadioGroup>
              </div>
              <label class="input-component mt-2" v-if="donation.dedicate">
                <!-- <strong>
                  Acknowledgement
                  <span class="font-normal text-secondary-text">(Optional)</span>
                </strong> -->
                <input
                  type="text"
                  v-model="donation.dedication"
                  placeholder="Example: ‘The Lee family’ or ‘John Smith’"
                  class="form-control"
                />
              </label>
              <label class="input-component mt-4">
                <strong>
                  Note
                  <span class="font-normal text-secondary-text">(Optional)</span>
                </strong>
                <input
                  type="text"
                  v-model="donation.notes"
                  placeholder="Add a note to your donation"
                  class="form-control"
                />
              </label>
            </div>
          </div>
        </div>

        <div class="payment-info" v-if="canMakeDonation">
          <h2 class="section-heading xlarge-text">Payment Information</h2>
          <!-- <p class="mb-5">We will send you a tax receipt by mail.</p> -->
          <p v-if="billingErrorMessage" class="font-semibold text-red-500 mb-4">
            <span>{{ billingErrorMessage }}</span>
          </p>
          <template v-if="savedPayments.length || siteConfig.features.achPaymentsEnabled">
            <RadioGroup v-model="selectedPayment" class="saved-payments">
              <RadioGroupOption
                v-for="payment in savedPayments"
                :value="payment"
                class="payment-box"
                v-slot="{ checked }"
                as="label"
              >
                <input type="radio" :checked="checked" />
                <p>
                  <!--                  <strong class="md:xlarge-text" v-html="payment.type"></strong>-->
                  <span> Account ending in {{ payment.endingNumber }} </span>
                  <!--                  <span v-html="payment.holder"></span>-->
                  <span v-if="payment.expireDate"> Expires {{ payment.expireDate }} </span>
                </p>
              </RadioGroupOption>
              <RadioGroupOption :value="null" class="payment-box" v-slot="{ checked }" as="label">
                <input type="radio" :checked="checked" />
                Use a new Credit Card
              </RadioGroupOption>
              <RadioGroupOption v-if="siteConfig.features.achPaymentsEnabled" value="ACH" class="payment-box" v-slot="{ checked }" as="label">
                <input type="radio" :checked="checked" />
                Use ACH payment
              </RadioGroupOption>
            </RadioGroup>
          </template>
          <SwitchGroup
            v-if="paysProcessingFee"
          >
            <div class="grid items-center mb-4">
              <div class="flex items-center">
                <SwitchLabel class="mr-4">
                  (Cover {{ siteConfig.features.processingFeeRate * 100 }}% processing fee)
                </SwitchLabel>
                <Switch
                  v-model="acceptProcessingFee"
                  :class="acceptProcessingFee ? 'bg-accent-1' : 'bg-gray-200'"
                  class=" relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent-1 focus:ring-offset-2"
                >
                  <span
                    :class="acceptProcessingFee ? 'translate-x-6' : 'translate-x-1'"
                    class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
                  />
                </Switch>
              </div>
              <p v-if="siteConfig.copy?.processingFee" class="my-2">
                {{ siteConfig.copy?.processingFee }}
              </p>
            </div>
          </SwitchGroup>
          <template v-if="selectedPayment === null">
            <div class="row">
              <div class="first column">
                <!-- <template v-if="savedPayments.length && signedIn">
                  <div class="saved-payments">
                    <label
                      class="payment-box"
                      v-for="(payment, index) in savedPayments"
                      :key="index"
                    >
                      <input
                        type="radio"
                        v-model="selectedPayment"
                        :value="payment.id"
                      />

                      <p>
                        <strong class="md:xlarge-text" v-html="payment.type"></strong>
                        <span> Debit card ending in {{ payment.endingNumber }} </span>
                        <span v-html="payment.holder"></span>
                        <span> Expires {{ payment.expireDate }} </span>
                      </p>
                    </label>

                    <label class="new-payment">
                      <input
                        type="radio"
                        v-model="selectedPayment"
                        value="new payment"
                      />
                      Use a new payment method
                    </label>
                  </div>
                </template> -->
                <!-- <template v-if="savedPayments.length && signedIn">
                  <RadioGroup v-model="selectedPayment" class="saved-payments">
                    <RadioGroupOption
                      v-for="payment in savedPayments"
                      :value="payment.id"
                      class="payment-box"
                      v-slot="{ checked }"
                      as="label"
                      >
                      <input type="radio" :checked="checked"/>
                      <p>
                        <strong class="md:xlarge-text" v-html="payment.type"></strong>
                        <span> Debit card ending in {{ payment.endingNumber }} </span>
                        <span v-html="payment.holder"></span>
                        <span> Expires {{ payment.expireDate }} </span>
                      </p>
                    </RadioGroupOption>
                    <RadioGroupOption :value="`new payment`" class="new-payment" v-slot="{ checked }" as="label">
                      <input type="radio" :checked="checked"/>
                      Use a new payment method
                    </RadioGroupOption>
                  </RadioGroup>
                </template> -->
                <!-- <template v-if="signedIn">
                  <template
                    v-if="!savedPayments.length || selectedPayment === 'new payment'"
                  > -->
                <div class="payment-box">
                  <label class="input-component">
                    <strong>Card Number</strong>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="Card Number"
                      v-model="billing.cardNumber"
                    />
                  </label>
                  <div class="box-row">
                    <label class="input-component">
                      <strong>Name On Card</strong>
                      <input
                        type="text"
                        class="form-control"
                        placeholder="First Last"
                        v-model="billing.cardName"
                      />
                    </label>
                    <label class="input-component">
                      <strong>Expire Date</strong>
                      <input
                        type="text"
                        class="form-control"
                        placeholder="mm/yy"
                        v-maska="`##/##`"
                        v-model="billing.expDate"
                      />
                    </label>
                    <label class="input-component">
                      <strong>CVV</strong>
                      <input
                        type="text"
                        class="form-control"
                        placeholder="123"
                        v-model="billing.cvmValue"
                        v-maska="`####`"
                      />
                    </label>
                  </div>
                </div>
                <!-- </template>
                </template> -->
              </div>
              <div class="second column">
                <!-- <button
                  class="toggle-address"
                  @click.prevent="showingAddress = !showingAddress" v-if="signedIn"
                  :class="{toggled: showingAddress}"
                >
                  <ChevronDownIcon class="icon" /> {{showingAddress ? 'Hide' : 'Show' }} Billing Address
                </button> -->
                <!-- <template v-if="showingAddress || !signedIn"> -->
                <div class="form-row">
                  <label class="input-component">
                    <strong>First Name</strong>
                    <input
                      type="text"
                      placeholder="First"
                      class="form-control"
                      v-model="billing.firstName"
                      required
                    />
                  </label>
                  <label class="input-component">
                    <strong>Last Name</strong>
                    <input
                      type="text"
                      placeholder="Last"
                      class="form-control"
                      v-model="billing.lastName"
                      required
                    />
                  </label>
                </div>
                <label class="input-component">
                  <strong>Address Line 1</strong>
                  <input
                    type="text"
                    placeholder="Address line 1"
                    class="form-control"
                    v-model="billing.address1"
                    required
                  />
                </label>
                <label class="input-component">
                  <strong>
                    Address Line 2
                    <span class="font-normal text-secondary-text">(Optional)</span>
                  </strong>
                  <input
                    type="text"
                    placeholder="Address line 2"
                    class="form-control"
                    v-model="billing.address2"
                  />
                </label>
                <div class="form-row">
                  <label class="input-component">
                    <strong>City</strong>
                    <input
                      type="text"
                      placeholder="City"
                      class="form-control"
                      v-model="billing.city"
                      required
                    />
                  </label>
                  <label class="input-component">
                    <strong>State</strong>
                    <input
                      type="text"
                      placeholder="State"
                      class="form-control"
                      v-model="billing.state"
                      required
                    />
                  </label>
                </div>
                <div class="form-row">
                  <label class="input-component">
                    <strong>Zip Code</strong>
                    <input
                      type="text"
                      placeholder="Zip Code"
                      class="form-control"
                      v-model="billing.zip"
                      required
                    />
                  </label>
                  <label
                    class="input-component"
                    v-click-outside="closeCountriesDroplist"
                  >
                    <!-- <BaseDropdown
                      :dropdown="countriesDroplist"
                      class="countries-drop gray-bg"
                    /> -->
                    <!-- I had a bug with this that the listbox options doesn't close when it's inside the form -->
                    <!-- I tried to get it out of the form and it worked -->
                    <!-- I tried to prevent default or stop propagation of buttons but doesn't work properly -->
                    <!-- This solution is almost to use custom functions instead of headless real ones -->
                    <Listbox
                      v-model="countriesDroplist.selectedItem"
                      name="countriesDroplist"
                      class="base-listbox"
                      as="div"
                    >
                      <div
                        class="listbox-overlay"
                        v-if="countriesDroplist.optionsOpened"
                      ></div>
                      <div class="relative">
                        <ListboxLabel
                          class="inline-block small-bold mb-2"
                        >{{ countriesDroplist.label }}
                        </ListboxLabel>
                        <ListboxButton
                          class="listbox-button !bg-gray-50 !rounded-lg"
                          @click="countriesDroplist.optionsOpened = true"
                        >
                          <span class="block truncate">{{
                            countriesDroplist.selectedItem ||
                              countriesDroplist.options[0]
                          }}</span>
                          <span class="selector-icon">
                            <Icon
                              icon="heroicons-outline:selector"
                              class="h-5 w-5 text-secondary-text"
                            />
                          </span>
                        </ListboxButton>
                        <transition name="fade-fast">
                          <ListboxOptions
                            class="listbox-options"
                            static
                            v-show="countriesDroplist.optionsOpened"
                          >
                            <ListboxOption
                              v-slot="{ active }"
                              v-for="(option, index) in countriesDroplist.options"
                              :value="option"
                              :key="index"
                              as="template"
                            >
                              <li
                                class="listbox-option small-regular"
                                :class="[
                                  { 'bg-gray-50': active },
                                  {
                                    'font-bold text-accent-1':
                                      countriesDroplist.selectedItem === option,
                                  },
                                ]"
                                @click.prevent="selectCountry(option)"
                              >{{ option }}
                              </li>
                            </ListboxOption>
                          </ListboxOptions>
                        </transition>
                      </div>
                    </Listbox>
                  </label>
                </div>
                <div class="form-row" v-if="!userStore.loggedIn">
                  <label class="input-component">
                    <strong>Email</strong>
                    <input
                      type="email"
                      placeholder="email@example.com"
                      class="form-control"
                      v-model="billing.email"
                      required
                    />
                  </label>
                  <label class="input-component" v-if="!userStore.loggedIn">
                    <strong>Phone Number</strong>
                    <input
                      type="tel"
                      placeholder="215-555-1212"
                      class="form-control"
                      v-model="billing.phoneNumber"
                      required
                    />
                  </label>
                </div>
                <!-- </template> -->
              </div>
            </div>
          </template>
          <template v-if="selectedPayment === 'ACH'">
            <div class="payment-form">
              <label class="input-component">
                <strong>Routing Number</strong>
                <input
                  type="text"
                  class="form-control"
                  placeholder="Routing Number"
                  v-model="billing.routingNumber"
                />
              </label>
              <label class="input-component">
                <strong>Account Number</strong>
                <input
                  type="text"
                  class="form-control"
                  placeholder="Account Number"
                  v-model="billing.accountNumber"
                />
              </label>
              <label class="input-component">
                <strong>Account Owner Name</strong>
                <input
                  type="text"
                  class="form-control"
                  placeholder="Account Number"
                  v-model="billing.cardName"
                />
              </label>
              <!--              <label class="input-component">-->
              <!--                <BaseDropdown-->
              <!--                  :dropdown="{-->
              <!--                    label: 'Account Type',-->
              <!--                    selectedItem: billing.accountType,-->
              <!--                    direction: '',-->
              <!--                    options: ['Checking', 'Savings'],-->
              <!--                  }"-->
              <!--                  class="countries-drop gray-bg"-->
              <!--                  @updateSelected="billing.accountType = $event"></BaseDropdown>-->
              <!--              </label>-->
            </div>
          </template>
          <SwitchGroup v-if="((selectedPayment === null) && userStore.loggedIn) ">
            <div class="flex items-center py-4">
              <SwitchLabel class="mr-4">{{ savedPayments.length ? 'Update payment info' : 'Save payment info' }}</SwitchLabel>
              <Switch
                v-model="savePayment"
                :class="shouldSavePayment ? 'bg-accent-1' : 'bg-gray-200'"
                class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent-1 focus:ring-offset-2"
              >
                <span
                  :class="shouldSavePayment ? 'translate-x-6' : 'translate-x-1'"
                  class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
                />
              </Switch>
            </div>
          </SwitchGroup>
        </div>

        <footer class="footer-box" v-if="canMakeDonation">
          <button
            class="btn-submit btn-accent-1"
            @click.prevent="submitPayment"
            :disabled="!canSubmit"
          >
            {{
              submitting
                ? 'Please wait...'
                : effectiveAmount
                  ? `Donate &dollar;${parseFloat(effectiveAmount).toFixed(2)}`
                  : 'Donate'
            }}
          </button>
          <p
            class="page-description mt-10 text-sm"
            v-if="siteConfig.copy?.donate?.disclaimer"
            v-html="addLineBreaks(siteConfig.copy.donate.disclaimer)"
          ></p>
        </footer>
      </form>
    </div>
  </section>
</template>

<style>
@import url('./index.pcss');
</style>
